import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Sparkles, ChevronRight, ChevronDown, ArrowLeft } from "lucide-react";
import { apiClient } from "@/lib/api";
import { useTier } from "@/context/AuthContext";
import BottomNav from "@/components/BottomNav";

/**
 * RareValuesAll — P7 cross-category Rare Values Scan (admin-only).
 *
 * Read-only diagnostic dashboard. Fetches every category's field-level
 * findings (rare values / near-duplicate groups / unused options) via a
 * single call to GET /api/rare-values/all-categories and renders a
 * collapsible Category → Field tree. Every category header deep-links to
 * /categories/:id/edit?admin=1 where the per-category Merge / Delete
 * actions already live (P2 / P4). No mutations happen here.
 */
export default function RareValuesAll() {
  const { isAdmin } = useTier();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [openCats, setOpenCats] = useState({}); // { [cat_id]: bool }
  const [openFields, setOpenFields] = useState({}); // { [`${cat_id}::${field}`]: bool }

  const runScan = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await apiClient.get("/rare-values/all-categories");
      setReport(data);
      // Open all categories by default so users see findings without
      // extra clicks. Fields stay collapsed to keep the initial view calm.
      const seed = {};
      for (const c of data.categories || []) seed[c.category_id] = true;
      setOpenCats(seed);
      setOpenFields({});
    } catch (e) {
      setError(e?.response?.data?.detail || "Scan failed.");
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="max-w-3xl mx-auto px-5 sm:px-6 py-10">
        <p className="text-sm vp-muted">Admin only.</p>
        <BottomNav current="settings" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-6 py-8">
      <Link to="/settings" className="vp-btn vp-btn-ghost vp-btn-sm inline-flex items-center gap-1 mb-4">
        <ArrowLeft size={14} /> Back to Settings
      </Link>

      <section
        className="vp-card p-6 mb-6"
        data-testid="cb-cross-rare-values-scan-card"
      >
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Rare Values — All Categories</h1>
            <p className="text-xs vp-muted mt-1">
              Diagnostic scan across every category. Read-only — jump into a
              category to Merge / Delete.
            </p>
          </div>
          <button
            type="button"
            onClick={runScan}
            disabled={loading}
            data-testid="cb-cross-rare-values-run-btn"
            className="vp-btn vp-btn-secondary vp-btn-sm disabled:opacity-50"
          >
            {loading ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
            {report ? "Re-scan" : "Run Scan"}
          </button>
        </div>

        {error ? (
          <p className="text-xs mt-3 text-[hsl(var(--loss))]">{error}</p>
        ) : null}

        {report ? (
          <div className="mt-4 space-y-3">
            {(report.categories || []).length === 0 ? (
              <p className="text-sm vp-muted">No rare values, near-duplicates, or unused options across any category.</p>
            ) : (
              (report.categories || []).map((c) => {
                const catOpen = !!openCats[c.category_id];
                return (
                  <div
                    key={c.category_id}
                    className="border border-[hsl(var(--border))] rounded-md"
                    data-testid={`cb-cross-rare-values-category-${c.category_id}`}
                  >
                    <div className="flex items-center justify-between gap-2 px-3 py-2">
                      <button
                        type="button"
                        onClick={() =>
                          setOpenCats((prev) => ({ ...prev, [c.category_id]: !prev[c.category_id] }))
                        }
                        className="flex items-center gap-2 text-left flex-1 min-w-0"
                        data-testid={`cb-cross-rare-values-category-toggle-${c.category_id}`}
                      >
                        {catOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        <span className="text-sm font-semibold truncate">{c.category_name || "(unnamed)"}</span>
                        <span className="text-[10px] uppercase tracking-wider vp-muted">
                          {c.fields.length} field{c.fields.length === 1 ? "" : "s"} · {c.items_scanned} item{c.items_scanned === 1 ? "" : "s"}
                        </span>
                      </button>
                      <Link
                        to={`/categories/${c.category_id}/edit?admin=1`}
                        className="vp-btn vp-btn-ghost vp-btn-sm !h-7 !px-2 border border-[hsl(var(--border))] shrink-0"
                        data-testid={`cb-cross-rare-values-category-link-${c.category_id}`}
                      >
                        Open
                      </Link>
                    </div>

                    {catOpen ? (
                      <div className="border-t border-[hsl(var(--border))] p-3 space-y-2">
                        {c.fields.map((f) => {
                          const fk = `${c.category_id}::${f.field_name}`;
                          const fieldOpen = !!openFields[fk];
                          const rare = (f.values || []).filter((v) => v.rare);
                          return (
                            <div
                              key={fk}
                              className="border border-[hsl(var(--border))] rounded-md"
                              data-testid={`cb-cross-rare-values-field-${c.category_id}-${f.field_name}`}
                            >
                              <button
                                type="button"
                                onClick={() =>
                                  setOpenFields((prev) => ({ ...prev, [fk]: !prev[fk] }))
                                }
                                className="flex items-center gap-2 w-full text-left px-3 py-1.5"
                                data-testid={`cb-cross-rare-values-field-toggle-${c.category_id}-${f.field_name}`}
                              >
                                {fieldOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                                <span className="text-sm font-medium">{f.field_name}</span>
                                <span className="text-[10px] uppercase tracking-wider vp-muted">{f.field_type}</span>
                                {f.master ? (
                                  <span className="text-[10px] uppercase tracking-wider border border-[hsl(var(--gold))] text-[hsl(var(--gold))] rounded-full px-1.5">Master</span>
                                ) : null}
                                <span className="text-[10px] vp-muted ml-auto">
                                  {rare.length} rare · {(f.duplicate_groups || []).length} groups · {(f.unused_options || []).length} unused
                                </span>
                              </button>
                              {fieldOpen ? (
                                <div className="border-t border-[hsl(var(--border))] p-3 space-y-3 text-xs">
                                  {(f.duplicate_groups || []).length > 0 ? (
                                    <div>
                                      <p className="text-[10px] uppercase tracking-wider vp-muted mb-1">Near-duplicate groups</p>
                                      <ul className="space-y-1">
                                        {f.duplicate_groups.map((g) => (
                                          <li key={g.normalised} className="flex items-center flex-wrap gap-1.5">
                                            {g.variants.map((v) => (
                                              <span key={v.value} className="border border-[hsl(var(--border))] rounded-md px-2 py-0.5">
                                                {v.value} <span className="vp-muted">×{v.count}</span>
                                              </span>
                                            ))}
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  ) : null}
                                  {rare.length > 0 ? (
                                    <div>
                                      <p className="text-[10px] uppercase tracking-wider vp-muted mb-1">Rare values (used by 1 item)</p>
                                      <ul className="flex flex-wrap gap-1.5">
                                        {rare.map((v) => (
                                          <li key={v.value} className="border border-[hsl(var(--border))] rounded-md px-2 py-0.5">
                                            {v.value}
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  ) : null}
                                  {(f.unused_options || []).length > 0 ? (
                                    <div>
                                      <p className="text-[10px] uppercase tracking-wider vp-muted mb-1">Unused options</p>
                                      <ul className="flex flex-wrap gap-1.5">
                                        {f.unused_options.map((opt) => (
                                          <li key={opt} className="border border-[hsl(var(--border))] rounded-md px-2 py-0.5">
                                            {opt}
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  ) : null}
                                </div>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                );
              })
            )}
          </div>
        ) : (
          <p className="text-sm vp-muted mt-4">Click Run Scan to inspect every category.</p>
        )}
      </section>

      <BottomNav current="settings" />
    </div>
  );
}
